'use client';

import { useState } from 'react';
import { UninstallModal } from './uninstall-modal';
import useBus, { dispatch } from '../../hooks/use-bus';
import { AppBusEventType } from '../../interfaces/bus-events';
import { useDeepLink } from '../../hooks/use-deep-link';
import { useOverwolfStore } from '../../hooks/use-overwolf-store';
import { OwApp } from '../../interfaces/domain/ow-app.modal';

// -----------------------------------------------------------------------------
export const UninstallModalWrapper = (): JSX.Element => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [appName, setAppName] = useState<string>('');
  const [extensionId, setExtensionId] = useState<string>('');
  const { uninstallApp } = useDeepLink();
  const {
    isAvailable: isOverwolfStoreAvailable,
    uninstallApp: uninstallViaStore,
  } = useOverwolfStore();

  useBus(
    AppBusEventType.App.ShowUninstallModal,
    event => {
      const { data } = event;
      setAppName(data.appName as string);
      setExtensionId(data.extensionId as string);
      setIsOpen(true);
    },
  );

  const handleClose = (): void => {
    setIsOpen(false);
  };

  const handleUninstall = async (): Promise<void> => {
    // Inside the Overwolf client, uninstall through the shell bridge. We don't
    // refresh installed state here — the shell pushes an app-uninstalled event
    // once it completes, which refreshes the installed set from the source of
    // truth (see InstalledExtensionsProvider). The deep-link "client not
    // installed" fallback doesn't apply here — the client is clearly present.
    if (isOverwolfStoreAvailable) {
      uninstallViaStore(extensionId);
      setIsOpen(false);
      return;
    }

    const result = await uninstallApp(extensionId);
    if (result.linkType !== 'deepLink') {
      dispatch({
        type: AppBusEventType.App.ShowDownloadModal,
        data: {
          isOpen: true,
          currentApp: {
            id: extensionId,
            title: appName,
            isElectron: false,
          } as OwApp,
          url: null,
          isDeepLink: false,
        },
      });
    }
    setIsOpen(false);
  };

  return (
    <UninstallModal
      isOpen={isOpen}
      appName={appName}
      onClickClose={handleClose}
      onClickUninstall={handleUninstall}
    />
  );
};
